import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, ComposedChart, ReferenceLine
} from "recharts";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Activity } from "lucide-react";

import { API } from "../config/api";

const TreasuryPage = () => {
  const [timeframe, setTimeframe] = useState('5d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const timeframes = [
    { key: '1d', label: '1D' },
    { key: '5d', label: '5D' },
    { key: '1mo', label: '1M' },
    { key: '3mo', label: '3M' },
    { key: '6mo', label: '6M' },
    { key: '1y', label: '1Y' },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/market/treasury`, {
        params: { timeframe },
        timeout: 45000
      });
      setData(res.data);
    } catch (err) {
      console.error("Treasury fetch error:", err);
      toast.error("Failed to load treasury data");
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const current = data?.current || {};
  const regression = data?.regression || {};
  const comparisons = data?.index_comparisons || [];
  const chartData = data?.chart_data || [];
  const capWeighted = data?.cap_weighted || {};
  const capChartData = capWeighted.chart_data || [];
  const isRising = regression.trend === 'RISING';

  // Compute tight Y-axis domain for treasury yield (±0.5% padding around data range)
  const yieldValues = chartData.map(p => p.treasury).filter(v => v != null);
  const yieldMin = yieldValues.length ? Math.floor((Math.min(...yieldValues) - 0.5) * 10) / 10 : 3;
  const yieldMax = yieldValues.length ? Math.ceil((Math.max(...yieldValues) + 0.5) * 10) / 10 : 5;

  const cwYieldValues = capChartData.map(p => p.treasury).filter(v => v != null);
  const cwYieldMin = cwYieldValues.length ? Math.floor((Math.min(...cwYieldValues) - 0.5) * 10) / 10 : yieldMin;
  const cwYieldMax = cwYieldValues.length ? Math.ceil((Math.max(...cwYieldValues) + 0.5) * 10) / 10 : yieldMax;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-[#0d0d15]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl font-bold text-white">10-Year Treasury Yield</h1>
              <p className="text-xs text-gray-400 mt-0.5">
                Yield vs. Major Indices Correlation Analysis
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Current yield */}
              <div className="text-right">
                <div className="text-2xl font-bold text-white">{current.yield?.toFixed(3) || '--'}%</div>
                <div className={`text-sm font-semibold ${current.change >= 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {current.change >= 0 ? '+' : ''}{current.change?.toFixed(3) || '--'} ({current.change_pct?.toFixed(2) || '--'}%)
                </div>
              </div>
            </div>
          </div>

          {/* Timeframe Buttons */}
          <div className="flex gap-1 mt-3">
            {timeframes.map(tf => (
              <button
                key={tf.key}
                onClick={() => setTimeframe(tf.key)}
                data-testid={`treasury-tf-${tf.key}`}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  timeframe === tf.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {/* Trend Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-[#111] border-gray-800">
            <CardContent className="p-3">
              <div className="text-[10px] text-gray-500 uppercase">Yield Trend</div>
              <div className={`text-lg font-bold ${isRising ? 'text-red-400' : 'text-green-400'}`}>
                {regression.trend || '--'}
              </div>
              <div className="text-[10px] text-gray-500">
                {isRising ? 'Rising yields pressure equities' : 'Falling yields support equities'}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-gray-800">
            <CardContent className="p-3">
              <div className="text-[10px] text-gray-500 uppercase">Trend Strength</div>
              <div className={`text-lg font-bold ${
                regression.r_squared >= 0.7 ? 'text-green-400' :
                regression.r_squared >= 0.5 ? 'text-yellow-400' : 'text-gray-400'
              }`}>
                {((regression.r_squared || 0) * 100).toFixed(0)}%
              </div>
              <div className="text-[10px] text-gray-500">R² Correlation</div>
              <div className={`text-[9px] mt-0.5 ${
                regression.r_squared >= 0.7 ? 'text-green-500' :
                regression.r_squared >= 0.5 ? 'text-yellow-500' : 'text-gray-500'
              }`}>
                {regression.r_squared >= 0.7 ? 'Strong trend' :
                 regression.r_squared >= 0.5 ? 'Moderate trend' :
                 regression.r_squared >= 0.3 ? 'Weak trend' : 'No clear trend'}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-gray-800">
            <CardContent className="p-3">
              <div className="text-[10px] text-gray-500 uppercase">Period Change</div>
              <div className={`text-lg font-bold ${current.change >= 0 ? 'text-red-400' : 'text-green-400'}`}>
                {current.change >= 0 ? '+' : ''}{current.change?.toFixed(3) || '--'}
              </div>
              <div className="text-[10px] text-gray-500">
                {current.change >= 0 ? 'Higher yields = tighter conditions' : 'Lower yields = easier conditions'}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-gray-800">
            <CardContent className="p-3">
              <div className="text-[10px] text-gray-500 uppercase">Market Impact</div>
              <div className={`text-lg font-bold ${
                comparisons.every(c => c.correlation < -0.3) ? 'text-yellow-400' : 'text-gray-400'
              }`}>
                {comparisons.every(c => c.correlation < -0.3) ? 'INVERSE' :
                 comparisons.every(c => c.correlation > 0.3) ? 'ALIGNED' : 'MIXED'}
              </div>
              <div className="text-[10px] text-gray-500">
                Overall correlation with equities
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Treasury Yield Chart */}
        <Card className="bg-[#111] border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-white">10-Year Treasury Yield</h2>
              <Badge className={isRising ? 'bg-red-900/60 text-red-300' : 'bg-green-900/60 text-green-300'}>
                {isRising ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                {regression.trend}
              </Badge>
            </div>
            <div className="h-[280px]">
              {chartData.length > 0 ? (
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                    <XAxis dataKey="time" tick={{ fill: '#666', fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis domain={[yieldMin, yieldMax]} tick={{ fill: '#666', fontSize: 10 }} tickFormatter={v => `${v}%`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }}
                      labelStyle={{ color: '#aaa' }}
                      formatter={(v) => [`${v}%`, '10Y Yield']}
                    />
                    <Line type="monotone" dataKey="treasury" stroke="#f59e0b" strokeWidth={2} dot={false} name="10Y Yield" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  {loading ? (
                    <div className="w-full h-full flex items-end gap-1 px-4 pb-4">
                      {Array.from({length:20}).map((_,i) => (
                        <div key={i} className="animate-pulse bg-gray-800/60 rounded-t flex-1" style={{height: `${15+Math.random()*60}%`}} />
                      ))}
                    </div>
                  ) : 'No data available'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Treasury vs Cap-Weighted Market Price */}
        <Card className="bg-[#111] border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-white">10Y Yield vs. Cap-Weighted Market Price</h2>
              {capWeighted.change_pct != null && (
                <Badge className={capWeighted.change_pct >= 0 ? 'bg-green-900/60 text-green-300' : 'bg-red-900/60 text-red-300'}>
                  Market {capWeighted.change_pct >= 0 ? '+' : ''}{capWeighted.change_pct}%
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-gray-400 mb-3">
              Cap-weighted composite of SPY, QQQ, DIA, IWM (amber = 10Y yield, cyan = market price).
              Divergence signals shifting risk sentiment.
            </p>
            <div className="h-[300px]">
              {capChartData.filter(p => p.market_price != null).length > 0 ? (
                <ResponsiveContainer>
                  <ComposedChart data={capChartData} margin={{ top: 5, right: 10, bottom: 5, left: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                    <XAxis dataKey="time" tick={{ fill: '#666', fontSize: 9 }} interval="preserveStartEnd" />
                    <YAxis yAxisId="yield" domain={[cwYieldMin, cwYieldMax]} tick={{ fill: '#f59e0b', fontSize: 10 }} tickFormatter={v => `${v}%`} />
                    <YAxis yAxisId="market" orientation="right" domain={['auto', 'auto']} tick={{ fill: '#22d3ee', fontSize: 10 }} tickFormatter={v => `$${v}`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8, fontSize: 11 }}
                      labelStyle={{ color: '#aaa' }}
                      formatter={(v, name) => name === '10Y Yield' ? [`${v}%`, name] : [`$${v}`, name]}
                    />
                    <Area yAxisId="market" type="monotone" dataKey="market_price" fill="#22d3ee10" stroke="#22d3ee" strokeWidth={2} dot={false} name="Market Price" connectNulls />
                    <Line yAxisId="yield" type="monotone" dataKey="treasury" stroke="#f59e0b" strokeWidth={2.5} dot={false} name="10Y Yield" />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  {loading ? (
                    <div className="w-full h-full flex items-end gap-1 px-4 pb-4">
                      {Array.from({length:20}).map((_,i) => (
                        <div key={i} className="animate-pulse bg-gray-800/60 rounded-t flex-1" style={{height: `${15+Math.random()*60}%`}} />
                      ))}
                    </div>
                  ) : 'No data available'}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-4 mt-2 justify-center">
              <span className="text-[10px] flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-500 inline-block" /> 10Y Yield (left)</span>
              <span className="text-[10px] flex items-center gap-1"><span className="w-3 h-0.5 bg-cyan-400 inline-block" /> Cap-Weighted Market (right)</span>
              <span className="text-[9px] text-gray-500">SPY + QQQ + DIA + IWM</span>
            </div>
          </CardContent>
        </Card>

        {/* Index Comparison Table */}
        <Card className="bg-[#111] border-gray-800">
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold text-white mb-3">Treasury vs. Major Indices</h2>
            <p className="text-[10px] text-gray-400 mb-3">
              When Treasury yields rise, it typically puts downward pressure on stock prices as bonds become more attractive.
              A strong inverse correlation means stocks and yields are moving in opposite directions.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="treasury-comparison-table">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-400 text-xs">
                    <th className="text-left py-2 px-2">Index</th>
                    <th className="text-right py-2 px-2">Price</th>
                    <th className="text-right py-2 px-2">Change</th>
                    <th className="text-center py-2 px-2">Correlation</th>
                    <th className="text-center py-2 px-2">Relationship</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisons.map(comp => (
                    <tr key={comp.ticker} className="border-b border-gray-800/50 hover:bg-gray-900/30">
                      <td className="py-2.5 px-2">
                        <div className="font-medium text-white text-xs">{comp.name}</div>
                      </td>
                      <td className="py-2.5 px-2 text-right text-white text-xs">
                        ${comp.current_price?.toLocaleString()}
                      </td>
                      <td className="py-2.5 px-2 text-right">
                        <span className={`text-xs font-semibold flex items-center justify-end gap-0.5 ${
                          comp.change_pct >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {comp.change_pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {comp.change_pct >= 0 ? '+' : ''}{comp.change_pct}%
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <span className={`text-xs font-bold ${
                          comp.correlation < -0.5 ? 'text-red-400' :
                          comp.correlation > 0.5 ? 'text-green-400' : 'text-gray-400'
                        }`}>
                          {comp.correlation?.toFixed(3)}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <Badge className={`text-[10px] ${
                          comp.relationship === 'Inverse' ? 'bg-red-900/50 text-red-300' :
                          comp.relationship === 'Positive' ? 'bg-green-900/50 text-green-300' :
                          'bg-gray-800 text-gray-400'
                        }`}>
                          {comp.relationship}
                        </Badge>
                        <div className="text-[9px] text-gray-500 mt-0.5">{comp.relationship_desc}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Overlay Chart: Treasury vs Index */}
        <Card className="bg-[#111] border-gray-800">
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold text-white mb-3">Yield vs. Indices Overlay</h2>
            <p className="text-[10px] text-gray-400 mb-3">
              10Y Treasury Yield (left axis) plotted against normalized index performance (right axis).
              When lines diverge, it signals potential market stress.
            </p>
            <div className="h-[320px]">
              {chartData.length > 0 ? (
                <ResponsiveContainer>
                  <ComposedChart data={(() => {
                    // Find first non-null value for each index to use as baseline
                    const baselines = {};
                    for (const comp of comparisons) {
                      for (const p of chartData) {
                        if (p[comp.key] != null) {
                          baselines[comp.key] = p[comp.key];
                          break;
                        }
                      }
                    }
                    return chartData.map(p => {
                      const normalized = { ...p };
                      for (const comp of comparisons) {
                        const baseVal = baselines[comp.key];
                        const currVal = p[comp.key];
                        if (baseVal && currVal != null) {
                          normalized[`${comp.key}_norm`] = parseFloat(((currVal - baseVal) / baseVal * 100).toFixed(2));
                        }
                      }
                      return normalized;
                    });
                  })()} margin={{ top: 5, right: 10, bottom: 5, left: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1a1a2e" />
                    <XAxis dataKey="time" tick={{ fill: '#666', fontSize: 9 }} interval="preserveStartEnd" />
                    <YAxis yAxisId="yield" domain={[yieldMin, yieldMax]} tick={{ fill: '#f59e0b', fontSize: 10 }} tickFormatter={v => `${v}%`} />
                    <YAxis yAxisId="index" orientation="right" tick={{ fill: '#888', fontSize: 10 }} tickFormatter={v => `${v}%`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: 8, fontSize: 11 }}
                      labelStyle={{ color: '#aaa' }}
                    />
                    <ReferenceLine yAxisId="index" y={0} stroke="#333" strokeDasharray="3 3" />
                    <Line yAxisId="yield" type="monotone" dataKey="treasury" stroke="#f59e0b" strokeWidth={2.5} dot={false} name="10Y Yield (%)" />
                    <Line yAxisId="index" type="monotone" dataKey="sp_500_norm" stroke="#3b82f6" strokeWidth={1.5} dot={false} name="S&P 500 (%)" connectNulls />
                    <Line yAxisId="index" type="monotone" dataKey="nasdaq_100_norm" stroke="#8b5cf6" strokeWidth={1.5} dot={false} name="NASDAQ (%)" connectNulls />
                    <Line yAxisId="index" type="monotone" dataKey="dow_30_norm" stroke="#10b981" strokeWidth={1.5} dot={false} name="DOW (%)" connectNulls />
                    <Line yAxisId="index" type="monotone" dataKey="russell_2000_norm" stroke="#ef4444" strokeWidth={1.5} dot={false} name="Russell (%)" connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  {loading ? (
                    <div className="w-full h-full flex items-end gap-1 px-4 pb-4">
                      {Array.from({length:20}).map((_,i) => (
                        <div key={i} className="animate-pulse bg-gray-800/60 rounded-t flex-1" style={{height: `${15+Math.random()*60}%`}} />
                      ))}
                    </div>
                  ) : 'No data available'}
                </div>
              )}
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-3 mt-2 justify-center">
              <span className="text-[10px] flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-500 inline-block" /> 10Y Yield</span>
              <span className="text-[10px] flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-500 inline-block" /> S&P 500</span>
              <span className="text-[10px] flex items-center gap-1"><span className="w-3 h-0.5 bg-purple-500 inline-block" /> NASDAQ</span>
              <span className="text-[10px] flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-500 inline-block" /> DOW</span>
              <span className="text-[10px] flex items-center gap-1"><span className="w-3 h-0.5 bg-red-500 inline-block" /> Russell</span>
            </div>
          </CardContent>
        </Card>

        {/* Educational Note */}
        <Card className="bg-[#0d0d15] border-gray-800">
          <CardContent className="p-4">
            <h3 className="text-xs font-semibold text-gray-300 mb-2">Understanding Treasury Yields & Stocks</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[10px] text-gray-400">
              <div>
                <div className="text-amber-400 font-semibold mb-1">Rising Yields</div>
                Higher yields make bonds more attractive relative to stocks. This increases borrowing costs,
                reduces corporate earnings forecasts, and typically pressures equity valuations — especially growth stocks (NASDAQ).
              </div>
              <div>
                <div className="text-green-400 font-semibold mb-1">Falling Yields</div>
                Lower yields signal easier financial conditions or economic slowdown fears.
                Stocks generally benefit as bonds become less competitive and future earnings are discounted at lower rates.
              </div>
              <div>
                <div className="text-blue-400 font-semibold mb-1">Correlation Breakdown</div>
                When the usual inverse correlation between yields and stocks breaks down,
                it often signals unusual market conditions — like stagflation (both falling) or a risk-on rally (both rising).
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TreasuryPage;
